import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { Node } from "@react-types/shared";
import {
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
    type ReactNode,
} from "react";
import type * as React from "react";
import { useGridList, useGridListItem } from "react-aria";
import { Item, useListState, type ListState } from "react-stately";

// ============================================================================
// Collection Context
// ============================================================================

const ITEM_DATA_ATTR = "data-list-item";

type ItemData = {
    id: string;
    ref: React.RefObject<HTMLElement>;
    disabled?: boolean;
};

type CollectionContextValue = {
    collectionRef: React.RefObject<HTMLElement>;
    itemMap: Map<string, ItemData>;
    registerItem: (id: string, data: ItemData) => void;
    unregisterItem: (id: string) => void;
    getItems: () => ItemData[];
};

const CollectionContext = createContext<CollectionContextValue | null>(null);

const useCollectionContext = () => {
    const context = useContext(CollectionContext);
    if (!context) {
        throw new Error("List.Item must be used within a List");
    }
    return context;
};

// ============================================================================
// List Context (for shared state including React Aria)
// ============================================================================

type ListContextValue = {
    alwaysShowIcon: boolean;
    state: ListState<ItemData> | null;
    itemCount: number;
    registerEmpty: () => void;
    hasEmptyComponent: boolean;
    trackingId?: string;
};

const ListContext = createContext<ListContextValue>({
    alwaysShowIcon: false,
    state: null,
    itemCount: 0,
    registerEmpty: () => undefined,
    hasEmptyComponent: false,
});

const useListContext = () => useContext(ListContext);

// ============================================================================
// Slot Context (for sub-component registration)
// ============================================================================

type SlotType = "icon" | "title" | "description" | "content" | "actions";

type SlotContextValue = {
    registerSlot: (type: SlotType, node: ReactNode) => void;
    unregisterSlot: (type: SlotType) => void;
} | null;

const SlotContext = createContext<SlotContextValue>(null);

const useSlotContext = () => useContext(SlotContext);

// ============================================================================
// Types
// ============================================================================

export type ListProps = PropsWithClassName & {
    "aria-label": string;
    children: ReactNode;
    alwaysShowIcon?: boolean;
    /** Kebab-case identifier used as a prefix for data-tracking-id on interactive elements within items. */
    trackingId?: string;
};

export type ListItemProps = PropsWithClassName & {
    id: string;
    disabled?: boolean;
    children: ReactNode;
} & (
        | { to: string; onClick?: never }
        | { onClick: () => void; to?: never }
        | { to?: never; onClick?: never } // plain div case
    );

export type ListItemIconProps = PropsWithClassName &
    PropsWithChildren & {
        size?: number;
    };

export type ListItemTitleProps = PropsWithClassName & PropsWithChildren;
export type ListItemDescriptionProps = PropsWithClassName & PropsWithChildren;
export type ListItemContentProps = PropsWithClassName &
    PropsWithChildren & {
        /** Alignment of the content. Defaults to "left" */
        alignment?: "left" | "right";
    };

export type ListItemActionsProps = PropsWithClassName & {
    children: ReactNode;
};

export type ListEmptyProps = PropsWithClassName & PropsWithChildren;

// ============================================================================
// List Root
// ============================================================================

/**
 * A flexible list component with support for icons, titles, descriptions, content, and actions.
 *
 * @example
 * ```tsx
 * <List aria-label="Users">
 *   <List.Item id="1" onClick={() => console.log("clicked")}>
 *     <List.ItemIcon><UserIcon /></List.ItemIcon>
 *     <List.ItemTitle>John Doe</List.ItemTitle>
 *     <List.ItemDescription>Engineer</List.ItemDescription>
 *   </List.Item>
 * </List>
 * ```
 */
const ListRoot: FC<ListProps> & ListSlots = ({
    children,
    className,
    "aria-label": ariaLabel,
    alwaysShowIcon = false,
    trackingId,
}) => {
    const collectionRef = useRef<HTMLUListElement>(null);
    const itemMapRef = useRef(new Map<string, ItemData>());
    const [registeredItems, setRegisteredItems] = useState<ItemData[]>([]);
    const [hasEmptyComponent, setHasEmptyComponent] = useState(false);

    const registerEmpty = useCallback(() => {
        setHasEmptyComponent(true);
    }, []);

    // Stable callbacks - itemMapRef.current is stable across renders
    const registerItem = useCallback((id: string, data: ItemData) => {
        const map = itemMapRef.current;
        const hadItem = map.has(id);
        map.set(id, data);
        // Only trigger re-render if this is a new item
        if (!hadItem) {
            setRegisteredItems(Array.from(map.values()));
        }
    }, []);

    const unregisterItem = useCallback((id: string) => {
        const map = itemMapRef.current;
        const hadItem = map.has(id);
        map.delete(id);
        // Only trigger re-render if item was actually removed
        if (hadItem) {
            setRegisteredItems(Array.from(map.values()));
        }
    }, []);

    const getItems = useCallback((): ItemData[] => {
        const collectionNode = collectionRef.current;
        if (!collectionNode) return [];

        // Query DOM for all items with the data attribute
        const orderedNodes = Array.from(collectionNode.querySelectorAll(`[${ITEM_DATA_ATTR}]`));

        // Sort items by DOM order
        const items = Array.from(itemMapRef.current.values());
        return items.sort((a, b) => {
            const aIndex = orderedNodes.indexOf(a.ref.current as Element);
            const bIndex = orderedNodes.indexOf(b.ref.current as Element);
            return aIndex - bIndex;
        });
    }, []);

    // Build React Aria/Stately collection from registered items
    // This enables keyboard navigation after items have registered
    const state = useListState<ItemData>({
        items: registeredItems,
        children: (item) => <Item key={item.id}>{item.id}</Item>,
    });

    // React Aria hook for grid list accessibility
    const { gridProps } = useGridList({ "aria-label": ariaLabel }, state, collectionRef);

    const collectionValue = useMemo<CollectionContextValue>(
        () => ({
            collectionRef: collectionRef as React.RefObject<HTMLElement>,
            itemMap: itemMapRef.current,
            registerItem,
            unregisterItem,
            getItems,
        }),
        [registerItem, unregisterItem, getItems],
    );

    const listValue = useMemo<ListContextValue>(
        () => ({
            alwaysShowIcon,
            state,
            itemCount: registeredItems.length,
            registerEmpty,
            hasEmptyComponent,
            trackingId,
        }),
        [alwaysShowIcon, state, registeredItems.length, registerEmpty, hasEmptyComponent, trackingId],
    );

    // Check if we have any items registered
    const hasItems = registeredItems.length > 0;

    // Bootstrap render: no items registered yet, but children must render
    // so items and Empty can register themselves via context.
    // Wrap in a hidden <ul> so <li> elements have a valid DOM parent.
    if (!hasItems && !hasEmptyComponent) {
        return (
            <CollectionContext.Provider value={collectionValue}>
                <ListContext.Provider value={listValue}>
                    <ul ref={collectionRef} style={{ display: "none" }}>
                        {children}
                    </ul>
                </ListContext.Provider>
            </CollectionContext.Provider>
        );
    }

    return (
        <CollectionContext.Provider value={collectionValue}>
            <ListContext.Provider value={listValue}>
                {/* Always render the ul - hide it visually when empty */}
                <div
                    className={cn(
                        "@container",
                        // Hide it if all the children ulf ot
                        "hidden has-[>ul>*:not(.hidden)]:block",
                        // Hide if it doesn't have any items at all
                        { hidden: !hasItems },
                    )}
                >
                    <ul
                        {...gridProps}
                        ref={collectionRef}
                        className={cn(
                            "grid grid-cols-[auto_1fr_0fr_auto]",
                            "@xl:grid-cols-[auto_minmax(120px,1fr)_1fr_auto]",
                            listClassName,
                            "shadow-sm",
                            // Apply border radius to focus ring on first/last items
                            "[&>li:first-child_[data-list-interactive]]:rounded-t-xl",
                            "[&>li:last-child_[data-list-interactive]]:rounded-b-xl",
                            className,
                        )}
                    >
                        {children}
                    </ul>
                </div>
            </ListContext.Provider>
        </CollectionContext.Provider>
    );
};
ListRoot.displayName = "List";

const placeholderState = {
    collection: { getItem: () => null, size: 0 },
    selectionManager: { focusedKey: null },
    disabledKeys: new Set(),
} as unknown as ListState<ItemData>;

// ============================================================================
// List.Item
// ============================================================================

type Slots = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
    content: ReactNode;
    actions: ReactNode;
};

const ListItem: FC<ListItemProps> = ({ id, onClick, to, disabled = false, children, className }) => {
    const rowRef = useRef<HTMLLIElement>(null);
    const focusRef = useRef<HTMLElement>(null);
    const { registerItem, unregisterItem } = useCollectionContext();
    const { alwaysShowIcon, state, trackingId } = useListContext();
    const [isActionsOpen, setIsActionsOpen] = useState(false);

    // Slot registration state - sub-components register themselves via context
    const [slots, setSlots] = useState<Slots>({
        icon: null,
        title: null,
        description: null,
        content: null,
        actions: null,
    });

    const registerSlot = useCallback((type: SlotType, node: ReactNode) => {
        setSlots((prev) => ({ ...prev, [type]: node }));
    }, []);

    const unregisterSlot = useCallback((type: SlotType) => {
        setSlots((prev) => ({ ...prev, [type]: null }));
    }, []);

    const slotContextValue = useMemo<SlotContextValue>(
        () => ({ registerSlot, unregisterSlot }),
        [registerSlot, unregisterSlot],
    );

    // Register on mount, unregister on unmount
    useEffect(() => {
        registerItem(id, {
            id,
            ref: rowRef as React.RefObject<HTMLElement>,
            disabled,
        });
        return () => unregisterItem(id);
    }, [id, disabled, registerItem, unregisterItem]);

    // Get the node from React Stately collection for useGridListItem
    const node = state?.collection.getItem(id) as Node<ItemData> | undefined;

    // Create a placeholder node for the first render before items register
    // This allows us to always call the hook (React rules of hooks)
    const placeholderNode: Node<ItemData> = useMemo(
        () => ({
            key: id,
            type: "item",
            value: { id, ref: focusRef, disabled },
            level: 0,
            hasChildNodes: false,
            rendered: null,
            textValue: id,
            "aria-label": undefined,
            index: 0,
            parentKey: null,
            prevKey: null,
            nextKey: null,
            props: {},
            childNodes: [],
        }),
        [id, disabled],
    );

    // React Aria hook for keyboard navigation
    // Use real state/node when available, placeholder otherwise
    const { rowProps, gridCellProps } = useGridListItem(
        { node: node ?? placeholderNode },
        state ?? placeholderState,
        focusRef,
    );

    // Extract slots from registered context values
    const {
        icon: iconNode,
        title: titleNode,
        description: descriptionNode,
        content: contentNode,
        actions: actionsNode,
    } = slots;
    const allSlotsEmpty =
        iconNode == null && titleNode == null && descriptionNode == null && contentNode == null && actionsNode == null;

    // Safely extract children from a slot node
    const getSlotChildren = (node: ReactNode): ReactNode => {
        if (isValidElement(node) && node.props && "children" in node.props) {
            return (node.props as { children?: ReactNode }).children;
        }
        return node;
    };

    const commonClassName = cn(
        listItemClassName,
        "col-span-full grid grid-cols-subgrid items-center gap-2 p-4",
        className,
    );

    const innerContent = (
        <>
            {/* Column 1: Icon */}
            <div className={cn("hidden items-center justify-center @xl:flex", alwaysShowIcon && "flex")}>
                {iconNode}
            </div>
            {/* Column 2: Title/Description */}
            <div className="flex min-w-0 flex-col gap-0.5">
                {titleNode}
                {descriptionNode}
                {contentNode && <div className={cn("mt-1 @xl:hidden")}>{getSlotChildren(contentNode)}</div>}
            </div>
            {/* Column 3: Content */}
            <div className={cn("hidden items-center @xl:flex", !actionsNode && "@xl:col-span-2")}>{contentNode}</div>
            {/* Column 4: Actions */}
            <div
                className={cn("flex items-center justify-end", !actionsNode && "hidden")}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                data-tracking-id-none
            >
                {actionsNode && (
                    <ListItemActionsRenderer onOpenChange={setIsActionsOpen}>
                        {getSlotChildren(actionsNode)}
                    </ListItemActionsRenderer>
                )}
            </div>
        </>
    );

    // Render the actual interactive element with focusRef for React Aria
    let interactiveElement: ReactNode;

    if (to) {
        interactiveElement = (
            <Link
                ref={focusRef as React.RefObject<HTMLAnchorElement>}
                to={to}
                className={commonClassName}
                data-active={isActionsOpen}
                data-list-interactive=""
                data-tracking-id={trackingId ? `${trackingId}-item-link` : "list-item"}
            >
                {innerContent}
            </Link>
        );
    } else if (onClick) {
        interactiveElement = (
            <button
                ref={focusRef as React.RefObject<HTMLButtonElement>}
                type="button"
                onClick={onClick}
                className={cn(commonClassName, "cursor-pointer text-left")}
                data-active={isActionsOpen}
                data-list-interactive=""
                disabled={disabled}
                data-tracking-id={trackingId ? `${trackingId}-item-button` : "list-item"}
            >
                {innerContent}
            </button>
        );
    } else {
        interactiveElement = (
            <div
                ref={focusRef as React.RefObject<HTMLDivElement>}
                className={commonClassName}
                tabIndex={0}
                data-active={isActionsOpen}
                data-list-interactive=""
                data-tracking-id="list-item"
            >
                {innerContent}
            </div>
        );
    }

    // Use React Aria props if state is available, otherwise fallback
    const liProps = state && node ? { ...rowProps, [ITEM_DATA_ATTR]: id } : { role: "row", [ITEM_DATA_ATTR]: id };

    const cellProps = state && node ? gridCellProps : { role: "gridcell" };
    return (
        <SlotContext.Provider value={slotContextValue}>
            <li
                ref={rowRef}
                {...liProps}
                className={cn("col-span-full grid grid-cols-subgrid", { hidden: allSlotsEmpty })}
            >
                {/* Hidden container for children to mount and register slots */}
                <div style={{ display: "none" }}>{children}</div>
                <div {...cellProps} className="col-span-full grid grid-cols-subgrid">
                    {interactiveElement}
                </div>
            </li>
        </SlotContext.Provider>
    );
};
ListItem.displayName = "List.Item";

// ============================================================================
// Sub-components
// ============================================================================

const ListItemIcon: FC<ListItemIconProps> = ({ children, className, size = 32 }) => {
    const slotContext = useSlotContext();

    const content = useMemo(
        () =>
            children ? (
                <div
                    data-list-slot="icon"
                    className={cn("flex shrink-0 items-center justify-center rounded-full bg-surface-muted", className)}
                    style={{ width: size, height: size }}
                >
                    {children}
                </div>
            ) : null,
        [children, className, size],
    );

    useEffect(() => {
        if (slotContext && content) {
            slotContext.registerSlot("icon", content);
            return () => slotContext.unregisterSlot("icon");
        }
    }, [slotContext, content]);

    return null;
};
ListItemIcon.displayName = "List.ItemIcon";

const ListItemTitle: FC<ListItemTitleProps> = ({ children, className }) => {
    const slotContext = useSlotContext();

    const content = useMemo(
        () => (
            <Text data-list-slot="title" className={cn(listItemTitleClassName, className)}>
                {children}
            </Text>
        ),
        [children, className],
    );

    useEffect(() => {
        if (slotContext) {
            slotContext.registerSlot("title", content);
            return () => slotContext.unregisterSlot("title");
        }
    }, [slotContext, content]);

    return null;
};
ListItemTitle.displayName = "List.ItemTitle";

const ListItemDescription: FC<ListItemDescriptionProps> = ({ children, className }) => {
    const slotContext = useSlotContext();

    const content = useMemo(
        () => (
            <Text data-list-slot="description" className={cn(listItemDescriptionClassName, className)}>
                {children}
            </Text>
        ),
        [children, className],
    );

    useEffect(() => {
        if (slotContext) {
            slotContext.registerSlot("description", content);
            return () => slotContext.unregisterSlot("description");
        }
    }, [slotContext, content]);

    return null;
};
ListItemDescription.displayName = "List.ItemDescription";

const ListItemContentSlot: FC<ListItemContentProps> = ({ children, className, alignment = "left" }) => {
    const slotContext = useSlotContext();

    const content = useMemo(
        () => (
            <div
                data-list-slot="content"
                className={cn(
                    "flex grow items-center",
                    {
                        "justify-start": alignment === "left",
                        "justify-end": alignment === "right",
                    },
                    className,
                )}
            >
                {children}
            </div>
        ),
        [children, className, alignment],
    );

    useEffect(() => {
        if (slotContext) {
            slotContext.registerSlot("content", content);
            return () => slotContext.unregisterSlot("content");
        }
    }, [slotContext, content]);

    return null;
};
ListItemContentSlot.displayName = "List.ItemContent";

const ListItemActions: FC<ListItemActionsProps> = ({ children, className }) => {
    const slotContext = useSlotContext();

    const content = useMemo(
        () => (
            <div data-list-slot="actions" className={className}>
                {children}
            </div>
        ),
        [children, className],
    );

    useEffect(() => {
        if (slotContext) {
            slotContext.registerSlot("actions", content);
            return () => slotContext.unregisterSlot("actions");
        }
    }, [slotContext, content]);

    return null;
};
ListItemActions.displayName = "List.ItemActions";

type ListItemActionsRendererProps = {
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
};

const ListItemActionsRenderer: FC<ListItemActionsRendererProps> = ({ children, onOpenChange }) => {
    return (
        <DropdownActions
            button={<Button variant="ghost" size="sm" LeadingIcon={IconEllipsisHorizontal} aria-label="More actions" />}
            onOpenChange={onOpenChange}
            contentClassName="w-48"
        >
            {children}
        </DropdownActions>
    );
};

const ListEmpty: FC<ListEmptyProps> = ({ children, className }) => {
    const { itemCount, registerEmpty } = useListContext();

    // Register that an Empty component exists
    useEffect(() => {
        registerEmpty();
    }, [registerEmpty]);

    // Only render if no items registered
    if (itemCount > 0) {
        return null;
    }

    return (
        <div className={cn("flex grow flex-col items-center justify-center p-8 text-center", className)}>
            {children}
        </div>
    );
};
ListEmpty.displayName = "List.Empty";

// ============================================================================
import {
    listClassName,
    listItemClassName,
    listItemTitleClassName,
    listItemDescriptionClassName,
} from "@/components/flexkit/list/listClassNames";

// ============================================================================
// Compound Component Assembly
// ============================================================================

type ListSlots = {
    Item: typeof ListItem;
    ItemIcon: typeof ListItemIcon;
    ItemTitle: typeof ListItemTitle;
    ItemDescription: typeof ListItemDescription;
    ItemContent: typeof ListItemContentSlot;
    ItemActions: typeof ListItemActions;
    Empty: typeof ListEmpty;
};

ListRoot.Item = ListItem;
ListRoot.ItemIcon = ListItemIcon;
ListRoot.ItemTitle = ListItemTitle;
ListRoot.ItemDescription = ListItemDescription;
ListRoot.ItemContent = ListItemContentSlot;
ListRoot.ItemActions = ListItemActions;
ListRoot.Empty = ListEmpty;

export { ListRoot as List };
